/**
 * The preseason tournament (ADR-0137): 1v1, double elimination, one bracket a region. The server keeps each region's
 * players and results (`GET /api/tournament/:region`, set through the admin API); the page at /tournament draws the
 * bracket from them (`bracket.ts` `drawBracket`, shared with the admin app's Tournament page, ADR-0150).
 */

export const TOURNAMENT_REGIONS = ['eu', 'na'] as const;
export type TournamentRegion = (typeof TOURNAMENT_REGIONS)[number];
export const isTournamentRegion = (s: string): s is TournamentRegion =>
  (TOURNAMENT_REGIONS as readonly string[]).includes(s);

/** A match: `W<round>-<n>` (winners bracket), `L<round>-<n>` (losers bracket), `GF`, and `GF2` (the reset), from 1. */
export const TOURNAMENT_MATCH = /^(?:[WL][1-9]\d?-[1-9]\d{0,2}|GF2?)$/;

/** One match's result: games won by its top and bottom player. `live`: still being played; else the higher won. */
export interface TournamentResult {
  score: [number, number];
  live?: boolean;
}

export interface TournamentView {
  region: TournamentRegion;
  /** When the first match starts (ISO 8601): the page counts down to it. */
  startsAt: string;
  /**
   * The players in seed order (best first); null: a seat nobody has yet (TBD). The bracket is the next power of two
   * (4 at least); the seats past the list are byes.
   */
  players: (string | null)[];
  /** By match id (`TOURNAMENT_MATCH`). */
  results: Record<string, TournamentResult>;
}

/** `GET /api/tournament/:region`. */
export interface TournamentAnswer {
  tournament: TournamentView;
}

/**
 * A region's tournament before an admin sets it: November 1st (the preseason, ADR-0123 amended), EU at 19:00 CET and NA
 * at 18:00 on the US East Coast, sixteen seats to be filled.
 */
export const tournamentDefault = (region: TournamentRegion): TournamentView => ({
  region,
  startsAt: region === 'eu' ? '2026-11-01T18:00:00Z' : '2026-11-01T23:00:00Z',
  players: Array<null>(16).fill(null),
  results: {},
});
